import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Link } from "wouter";
import { useAuth } from "@workspace/admin-ui";
import { PageHeader } from "@/components/AppShell";
import { chainAdminApi } from "@/lib/callables";

export default function RoomsAvailabilityPage() {
  const { membership, loading: authLoading } = useAuth();
  const chainId = membership?.chainId ?? "";

  const overviewQ = useQuery({
    queryKey: ["bookings", "overview", chainId],
    queryFn: () => chainAdminApi.chainAdminBookingsOverview({ chainId }),
    enabled: !!chainId,
  });

  // `overviewQ.isLoading` (isPending && isFetching) is false while the query
  // is disabled — i.e. before `chainId` resolves from auth — because a
  // disabled query never enters a fetching state. Without folding in auth's
  // own loading flag and `isPending`, the table would flash "No pharmacies
  // found." during the real-world gap between mount and the membership
  // (and therefore chainId) becoming available.
  const isLoading = authLoading || overviewQ.isPending;

  return (
    <>
      <PageHeader
        title="Rooms & Availability"
        description="Chain-wide rooms and pharmacist availability across all pharmacies"
      />
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Pharmacy</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Rooms</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        Loading rooms &amp; availability overview…
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : !overviewQ.data?.items.length ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No pharmacies found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  overviewQ.data.items.map((item) => (
                    <TableRow key={item.pharmacyId}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.displayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.pharmacyId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {item.hasBookingSetup ? (
                          <Chip label="Set up" size="small" color="success" variant="outlined" />
                        ) : (
                          <Chip label="Not set up yet" size="small" color="default" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {item.hasBookingSetup ? item.roomCount : "—"}
                      </TableCell>
                      <TableCell align="right">
                        {item.hasBookingSetup && (
                          <Button
                            component={Link}
                            href={`/rooms/${item.pharmacyId}`}
                            variant="outlined"
                            size="small"
                          >
                            View
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </>
  );
}
